import { type ReactNode, useId, useState } from 'react';
import classNames from 'classnames';

import ChevronLine from '@/assets/icons/chevron_line.svg?component';
import { Card, type CardAccent, type CardRadius } from '@/components/atoms/Card';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { COPY_EXCLUDE_ATTR } from '@/utils/elementCapture';

import styles from './Disclosure.module.scss';

export type DisclosureSpacing = 'none' | 'default' | 'top' | 'bottom' | 'top-and-bottom';

interface DisclosurePanelProps {
    /** Referenced by the trigger's `aria-controls`. */
    id: string;
    isOpen: boolean;
    /**
     * Slides the height open and closed. The content stays mounted, hidden from assistive
     * tech and out of the tab order while closed, so a chart inside keeps its state.
     */
    animated?: boolean;
    className?: string;
    children?: ReactNode;
}

/**
 * The collapsible region on its own, for a trigger that lives elsewhere (a toolbar button,
 * a heading icon). The trigger owns `aria-expanded` and points `aria-controls` at `id`.
 * The region stays in the DOM while closed so that reference always resolves.
 */
export function DisclosurePanel(props: DisclosurePanelProps) {
    const { id, isOpen, animated = false, className, children } = props;

    if (animated) {
        return (
            <div
                id={id}
                className={classNames(styles.animated, className)}
                data-open={isOpen}
                aria-hidden={!isOpen}
                inert={!isOpen}>
                <div className={styles.animatedInner}>{children}</div>
            </div>
        );
    }

    return (
        <div id={id} className={className} hidden={!isOpen}>
            {children}
        </div>
    );
}

interface DisclosureProps {
    title: ReactNode;
    /** Controlled open state; pair it with `onOpenChange`. Left out, the disclosure owns it. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    defaultOpen?: boolean;
    /** `card` is a card with a full-width header; `inline` a chevron line in running content. */
    variant?: 'card' | 'inline';
    /** Card only: `compact` is the dense header of a list of rows. */
    size?: 'default' | 'compact';
    /** Card only: `sm` for an item card inside another card. */
    radius?: CardRadius;
    /** Card only: a coloured edge in `--card-accent`, set by the caller's class. */
    accent?: CardAccent;
    /**
     * Card only: content at the end of the header, outside the toggle button, such as status
     * pills or an action. The chevron after it toggles for the mouse; the title button stays
     * the one tab stop.
     */
    headerEnd?: ReactNode;
    /**
     * Keeps the content mounted while closed and slides it open, for content that should not
     * reload or redraw on every open; the content brings its own padding. Left out, it mounts
     * only while open.
     */
    animated?: boolean;
    spacing?: DisclosureSpacing;
    className?: string;
    /** On the toggle button, for a trigger that takes a tone, such as a warning link. */
    triggerClassName?: string;
    /** On the content region, for its own layout or spacing. */
    panelClassName?: string;
    children: ReactNode;
}

/** A card, or an inline line, whose button shows and hides its content. */
export default function Disclosure(props: DisclosureProps) {
    const {
        title,
        open,
        onOpenChange,
        defaultOpen = false,
        variant = 'card',
        size = 'default',
        radius,
        accent,
        headerEnd,
        animated = false,
        spacing = 'default',
        className,
        triggerClassName,
        panelClassName,
        children,
    } = props;
    const [ownOpen, setOwnOpen] = useState(defaultOpen);
    const isOpen = open ?? ownOpen;
    const panelId = useId();

    const toggle = () => {
        const next = !isOpen;
        if (open === undefined) setOwnOpen(next);
        onOpenChange?.(next);
    };

    const chevron = (
        <SvgIcon
            icon={ChevronLine}
            size="custom"
            customSize={variant === 'inline' ? 16 : 20}
            className={classNames(styles.chevron, { [styles.chevronOpen]: isOpen })}
            svgProps={{ 'aria-hidden': true }}
        />
    );
    const panel = (
        <DisclosurePanel
            id={panelId}
            isOpen={isOpen}
            animated={animated}
            className={classNames(
                variant === 'card' && !animated && styles.content,
                panelClassName
            )}>
            {(animated || isOpen) && children}
        </DisclosurePanel>
    );
    const spacingClass = styles[`spacing-${spacing}`];

    if (variant === 'inline') {
        return (
            <div className={classNames(styles.inline, spacingClass, className)}>
                <button
                    type="button"
                    className={classNames(styles.inlineTrigger, triggerClassName)}
                    onClick={toggle}
                    aria-expanded={isOpen}
                    aria-controls={panelId}>
                    {chevron}
                    <span>{title}</span>
                </button>
                {panel}
            </div>
        );
    }

    const trigger = (
        <button
            type="button"
            className={classNames(styles.trigger, triggerClassName)}
            onClick={toggle}
            aria-expanded={isOpen}
            aria-controls={panelId}>
            <span className={styles.title}>{title}</span>
            {headerEnd === undefined && chevron}
        </button>
    );

    return (
        <Card
            as="article"
            padding="none"
            radius={radius}
            accent={accent}
            className={classNames(
                styles.disclosure,
                size === 'compact' && styles.compact,
                spacingClass,
                className
            )}>
            {headerEnd === undefined ? (
                trigger
            ) : (
                <div className={styles.header}>
                    {trigger}
                    {headerEnd}
                    {/* A second click area for the mouse only, so the card stays one tab stop
                        and is announced once. */}
                    <button
                        type="button"
                        className={styles.chevronButton}
                        onClick={toggle}
                        tabIndex={-1}
                        aria-hidden="true"
                        {...{ [COPY_EXCLUDE_ATTR]: '' }}>
                        {chevron}
                    </button>
                </div>
            )}
            {panel}
        </Card>
    );
}
